import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import api from '../../services/apiService';
import { useCurrency } from '../../context/CurrencyContext';
import './AdminConfigurationPage.css';

const GATEWAY_INFO = {
  razorpay: {
    color: '#2D70E2',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M11.39 2L3 22h4.93l1.6-3.84h8.94L20 22h4.93L16.61 2h-5.22zm.85 12.16L14.5 8.41l2.26 5.75h-4.52z" fill="#2D70E2"/></svg>
    ),
    apiLabel: 'Key ID',
    secretLabel: 'Key Secret'
  },
  paypal: {
    color: '#003087',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 01-.633-.74L4.944 2.216A.774.774 0 015.708 1.6h6.378c2.118 0 3.59.557 4.376 1.656.367.514.6 1.084.697 1.697.1.64.05 1.404-.155 2.331v.001l-.004.02c-.001.002 0 .003 0 .005-.749 3.84-3.316 5.165-6.591 5.165H8.91a.952.952 0 00-.94.804l-.878 5.567-.249 1.585a.5.5 0 01-.493.42h-.003l.728-.013z" fill="#003087"/><path d="M19.337 7.552c-.01.066-.023.133-.036.2C18.39 12.085 15.608 13.5 12.1 13.5H10.55a.95.95 0 00-.94.8l-.97 6.155-.275 1.743a.415.415 0 00.41.482h2.88a.668.668 0 00.66-.563l.027-.14.523-3.32.034-.183a.668.668 0 01.66-.563h.42c2.692 0 4.8-1.094 5.416-4.259.257-1.321.124-2.424-.557-3.2a2.656 2.656 0 00-.762-.553c-.078-.036-.158-.069-.24-.1z" fill="#0070E0"/></svg>
    ),
    apiLabel: 'Client ID',
    secretLabel: 'Client Secret',
    hasMode: true
  },
  stripe: {
    color: '#635BFF',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.918 3.757 7.076c0 3.483 2.129 4.95 5.59 6.318 2.226.876 2.998 1.496 2.998 2.483 0 .93-.793 1.487-2.198 1.487-1.867 0-4.817-.97-6.735-2.224l-.896 5.555C4.394 22.024 7.344 23 10.79 23c2.638 0 4.736-.636 6.244-1.836 1.646-1.305 2.519-3.2 2.519-5.533 0-3.564-2.178-4.998-5.577-6.482z" fill="#635BFF"/></svg>
    ),
    apiLabel: 'Publishable Key',
    secretLabel: 'Secret Key'
  },
  paytm: {
    color: '#00BAF2',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="12" rx="2" stroke="#00BAF2" strokeWidth="2" fill="none"/><path d="M6 15V9h2.5a2 2 0 010 4H7" stroke="#00BAF2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 9v6m0-6h3m-3 3h2" stroke="#00BAF2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
    ),
    apiLabel: 'Merchant ID',
    secretLabel: 'Merchant Key',
    hasMode: true
  }
};

const AdminConfigurationPage = () => {
  const { refreshCurrencies } = useCurrency();
  // SMTP State
  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: '',
    senderEmail: '',
    senderPassword: '',
    encryption: 'TLS'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Payment Gateway State
  const [gateways, setGateways] = useState([]);
  const [gatewayForms, setGatewayForms] = useState({});
  const [gatewayErrors, setGatewayErrors] = useState({});
  const [gatewayMessage, setGatewayMessage] = useState({});
  const [gatewaySaving, setGatewaySaving] = useState({});
  const [gatewayTesting, setGatewayTesting] = useState({});
  const [gatewaysLoading, setGatewaysLoading] = useState(true);

  // Firebase SMS State
  const [firebaseForm, setFirebaseForm] = useState({
    apiKey: '',
    authDomain: '',
    projectId: '',
    messagingSenderId: '',
    isEnabled: false
  });
  const [firebaseErrors, setFirebaseErrors] = useState({});
  const [firebaseMessage, setFirebaseMessage] = useState({ type: '', text: '' });
  const [firebaseSaving, setFirebaseSaving] = useState(false);
  const [firebaseLoading, setFirebaseLoading] = useState(true);

  // ============ CURRENCY STATE ============
  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currencyMessage, setCurrencyMessage] = useState({ type: '', text: '' });
  const [showCurrencyForm, setShowCurrencyForm] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [currencyForm, setCurrencyForm] = useState({ code: '', name: '', symbol: '', exchangeRate: '1', isEnabled: true, isDefault: false });
  const [currencyErrors, setCurrencyErrors] = useState({});
  const [currencySaving, setCurrencySaving] = useState(false);

  // ============ OPTIONS STATE ============
  const [optionTypes, setOptionTypes] = useState([]);
  const [selectedOptionType, setSelectedOptionType] = useState('country');
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionMessage, setOptionMessage] = useState({ type: '', text: '' });
  const [showOptionForm, setShowOptionForm] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [optionForm, setOptionForm] = useState({ type: 'country', name: '', value: '', extra: '', isEnabled: true });
  const [optionErrors, setOptionErrors] = useState({});
  const [optionSaving, setOptionSaving] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [optionSearch, setOptionSearch] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchSmtpSettings();
    fetchGateways();
    fetchFirebaseConfig();
    fetchCurrencies();
    fetchOptionTypes();
  }, []);

  // ============ SMTP ============
  const fetchSmtpSettings = async () => {
    try {
      const response = await api.get('/configuration/smtp');
      if (response.data.settings) {
        const s = response.data.settings;
        setFormData({
          smtpHost: s.smtpHost || '',
          smtpPort: s.smtpPort?.toString() || '',
          senderEmail: s.senderEmail || '',
          senderPassword: s.senderPassword || '',
          encryption: s.encryption || 'TLS'
        });
      }
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.smtpHost.trim()) newErrors.smtpHost = 'SMTP Host is required';
    if (!formData.smtpPort.trim()) newErrors.smtpPort = 'SMTP Port is required';
    else if (isNaN(formData.smtpPort) || parseInt(formData.smtpPort) < 1 || parseInt(formData.smtpPort) > 65535) {
      newErrors.smtpPort = 'Port must be between 1 and 65535';
    }
    if (!formData.senderEmail.trim()) newErrors.senderEmail = 'Email Address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.senderEmail)) {
      newErrors.senderEmail = 'Invalid email format';
    }
    if (!formData.senderPassword.trim()) newErrors.senderPassword = 'Email Password is required';
    if (!formData.encryption) newErrors.encryption = 'Encryption type is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/configuration/smtp', formData);
      setMessage({ type: 'success', text: response.data.message || 'SMTP settings saved successfully!' });
      if (response.data.settings) {
        setFormData(prev => ({ ...prev, senderPassword: response.data.settings.senderPassword }));
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Error saving SMTP settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!validate()) return;

    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/configuration/smtp/test', formData);
      setMessage({ type: 'success', text: response.data.message || 'SMTP connection successful!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'SMTP connection failed' });
    } finally {
      setTesting(false);
    }
  };

  // ============ PAYMENT GATEWAYS ============
  const fetchGateways = async () => {
    try {
      const response = await api.get('/payment-gateway/admin/gateways');
      const gws = response.data.gateways || [];
      setGateways(gws);
      const forms = {};
      gws.forEach(gw => {
        forms[gw.name] = {
          isEnabled: gw.isEnabled,
          apiKey: gw.apiKey || '',
          secretKey: gw.secretKey || '',
          mode: gw.mode || 'test'
        };
      });
      setGatewayForms(forms);
    } catch (error) {
      console.error('Error fetching gateways:', error);
    } finally {
      setGatewaysLoading(false);
    }
  };

  const handleGatewayChange = (gwName, field, value) => {
    setGatewayForms(prev => ({
      ...prev,
      [gwName]: { ...prev[gwName], [field]: value }
    }));
    if (gatewayErrors[gwName]?.[field]) {
      setGatewayErrors(prev => ({
        ...prev,
        [gwName]: { ...prev[gwName], [field]: '' }
      }));
    }
  };

  const handleGatewayToggle = (gwName) => {
    const form = gatewayForms[gwName];
    const newEnabled = !form.isEnabled;
    // If enabling, validate keys first
    if (newEnabled && (!form.apiKey || form.apiKey.includes('••••'))) {
      setGatewayErrors(prev => ({
        ...prev,
        [gwName]: { ...prev[gwName], apiKey: 'API Key is required to enable' }
      }));
      return;
    }
    if (newEnabled && (!form.secretKey || form.secretKey.includes('••••'))) {
      setGatewayErrors(prev => ({
        ...prev,
        [gwName]: { ...prev[gwName], secretKey: 'Secret Key is required to enable' }
      }));
      return;
    }
    handleGatewayChange(gwName, 'isEnabled', newEnabled);
  };

  const validateGateway = (gwName) => {
    const form = gatewayForms[gwName];
    const errs = {};
    if (form.isEnabled) {
      if (!form.apiKey || !form.apiKey.trim()) errs.apiKey = 'API Key is required';
      if (!form.secretKey || !form.secretKey.trim()) errs.secretKey = 'Secret Key is required';
    }
    setGatewayErrors(prev => ({ ...prev, [gwName]: errs }));
    return Object.keys(errs).length === 0;
  };

  const handleGatewaySave = async (gwName) => {
    if (!validateGateway(gwName)) return;
    setGatewaySaving(prev => ({ ...prev, [gwName]: true }));
    setGatewayMessage(prev => ({ ...prev, [gwName]: null }));

    try {
      const response = await api.put(`/payment-gateway/admin/gateways/${gwName}`, gatewayForms[gwName]);
      setGatewayMessage(prev => ({
        ...prev,
        [gwName]: { type: 'success', text: response.data.message || 'Settings saved!' }
      }));
      // Update local form with masked values
      if (response.data.gateway) {
        const gw = response.data.gateway;
        setGatewayForms(prev => ({
          ...prev,
          [gwName]: {
            isEnabled: gw.isEnabled,
            apiKey: gw.apiKey,
            secretKey: gw.secretKey,
            mode: gw.mode
          }
        }));
      }
    } catch (error) {
      setGatewayMessage(prev => ({
        ...prev,
        [gwName]: { type: 'error', text: error.response?.data?.error || 'Error saving settings' }
      }));
    } finally {
      setGatewaySaving(prev => ({ ...prev, [gwName]: false }));
    }
  };

  const handleGatewayTest = async (gwName) => {
    if (!validateGateway(gwName)) return;
    setGatewayTesting(prev => ({ ...prev, [gwName]: true }));
    setGatewayMessage(prev => ({ ...prev, [gwName]: null }));

    try {
      const response = await api.post(`/payment-gateway/admin/gateways/${gwName}/test`, gatewayForms[gwName]);
      setGatewayMessage(prev => ({
        ...prev,
        [gwName]: { type: 'success', text: response.data.message || 'Connection successful!' }
      }));
    } catch (error) {
      setGatewayMessage(prev => ({
        ...prev,
        [gwName]: { type: 'error', text: error.response?.data?.error || 'Connection test failed' }
      }));
    } finally {
      setGatewayTesting(prev => ({ ...prev, [gwName]: false }));
    }
  };

  // ============ FIREBASE SMS ============
  const fetchFirebaseConfig = async () => {
    try {
      const response = await api.get('/sms-verification/admin/config');
      if (response.data.config) {
        const c = response.data.config;
        setFirebaseForm({
          apiKey: c.apiKey || '',
          authDomain: c.authDomain || '',
          projectId: c.projectId || '',
          messagingSenderId: c.messagingSenderId || '',
          isEnabled: c.isEnabled || false
        });
      }
    } catch (error) {
      console.error('Error fetching Firebase config:', error);
    } finally {
      setFirebaseLoading(false);
    }
  };

  const handleFirebaseChange = (e) => {
    const { name, value } = e.target;
    setFirebaseForm(prev => ({ ...prev, [name]: value }));
    if (firebaseErrors[name]) {
      setFirebaseErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFirebaseToggle = () => {
    setFirebaseForm(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
  };

  const validateFirebase = () => {
    const errs = {};
    if (!firebaseForm.apiKey.trim()) errs.apiKey = 'Firebase API Key is required';
    if (!firebaseForm.authDomain.trim()) errs.authDomain = 'Auth Domain is required';
    else if (!firebaseForm.authDomain.includes('.')) errs.authDomain = 'Invalid Auth Domain format';
    if (!firebaseForm.projectId.trim()) errs.projectId = 'Project ID is required';
    if (!firebaseForm.messagingSenderId.trim()) errs.messagingSenderId = 'Messaging Sender ID is required';
    setFirebaseErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFirebaseSave = async () => {
    if (!validateFirebase()) return;
    setFirebaseSaving(true);
    setFirebaseMessage({ type: '', text: '' });

    try {
      const response = await api.post('/sms-verification/admin/config', firebaseForm);
      setFirebaseMessage({ type: 'success', text: response.data.message || 'Firebase configuration saved!' });
      if (response.data.config) {
        const c = response.data.config;
        setFirebaseForm({
          apiKey: c.apiKey || '',
          authDomain: c.authDomain || '',
          projectId: c.projectId || '',
          messagingSenderId: c.messagingSenderId || '',
          isEnabled: c.isEnabled || false
        });
      }
    } catch (error) {
      setFirebaseMessage({ type: 'error', text: error.response?.data?.error || 'Error saving Firebase configuration' });
    } finally {
      setFirebaseSaving(false);
    }
  };

  // ============ CURRENCIES ============
  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await api.get('/settings/currencies');
      setCurrencies(response.data || []);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setCurrenciesLoading(false);
    }
  }, []);

  const resetCurrencyForm = () => {
    setCurrencyForm({ code: '', name: '', symbol: '', exchangeRate: '1', isEnabled: true, isDefault: false });
    setCurrencyErrors({});
    setEditingCurrency(null);
    setShowCurrencyForm(false);
  };

  const handleEditCurrency = (c) => {
    setEditingCurrency(c);
    setCurrencyForm({ code: c.code, name: c.name, symbol: c.symbol, exchangeRate: String(c.exchangeRate), isEnabled: c.isEnabled, isDefault: c.isDefault });
    setCurrencyErrors({});
    setShowCurrencyForm(true);
  };

  const handleSaveCurrency = async () => {
    const errs = {};
    if (!currencyForm.code.trim()) errs.code = 'Currency code is required';
    else if (currencyForm.code.trim().length > 5) errs.code = 'Code must be 5 chars or less';
    if (!currencyForm.name.trim()) errs.name = 'Currency name is required';
    if (!currencyForm.symbol.trim()) errs.symbol = 'Symbol is required';
    if (!currencyForm.exchangeRate || isNaN(currencyForm.exchangeRate) || parseFloat(currencyForm.exchangeRate) <= 0) {
      errs.exchangeRate = 'Valid exchange rate is required';
    }
    setCurrencyErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setCurrencySaving(true);
    setCurrencyMessage({ type: '', text: '' });
    try {
      if (editingCurrency) {
        await api.put(`/settings/currencies/${editingCurrency.id}`, currencyForm);
        setCurrencyMessage({ type: 'success', text: 'Currency updated successfully!' });
      } else {
        await api.post('/settings/currencies', currencyForm);
        setCurrencyMessage({ type: 'success', text: 'Currency added successfully!' });
      }
      resetCurrencyForm();
      fetchCurrencies();
      await refreshCurrencies();
    } catch (error) {
      setCurrencyMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save currency' });
    } finally {
      setCurrencySaving(false);
    }
  };

  const handleDeleteCurrency = async (id) => {
    if (!window.confirm('Are you sure you want to delete this currency?')) return;
    try {
      await api.delete(`/settings/currencies/${id}`);
      setCurrencyMessage({ type: 'success', text: 'Currency deleted successfully!' });
      fetchCurrencies();
      await refreshCurrencies();
    } catch (error) {
      setCurrencyMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete currency' });
    }
  };

  const handleSetDefaultCurrency = async (id) => {
    try {
      await api.patch(`/settings/currencies/${id}/default`);
      setCurrencyMessage({ type: 'success', text: 'Default currency updated! Changes applied globally.' });
      fetchCurrencies();
      // Refresh global CurrencyContext so all price displays update instantly
      await refreshCurrencies();
    } catch (error) {
      setCurrencyMessage({ type: 'error', text: error.response?.data?.error || 'Failed to set default' });
    }
  };

  // ============ SYSTEM OPTIONS ============
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOptionTypes = useCallback(async () => {
    try {
      const response = await api.get('/settings/options/types');
      const types = response.data || [];
      setOptionTypes(types);
      if (types.length > 0 && !types.includes(selectedOptionType)) {
        setSelectedOptionType(types[0]);
      }
      // Fetch options for the selected type
      fetchOptions(types.length > 0 ? (types.includes('country') ? 'country' : types[0]) : 'country');
    } catch (error) {
      console.error('Error fetching option types:', error);
      fetchOptions('country');
    }
  }, []);

  const fetchOptions = async (type) => {
    try {
      setOptionsLoading(true);
      const response = await api.get(`/settings/options/${type || selectedOptionType}`);
      setOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching options:', error);
      setOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedOptionType(type);
    setOptionSearch('');
    fetchOptions(type);
    setShowOptionForm(false);
    setEditingOption(null);
  };

  const resetOptionForm = () => {
    setOptionForm({ type: selectedOptionType, name: '', value: '', extra: '', isEnabled: true });
    setOptionErrors({});
    setEditingOption(null);
    setShowOptionForm(false);
  };

  const handleEditOption = (opt) => {
    setEditingOption(opt);
    setOptionForm({ type: opt.type, name: opt.name, value: opt.value || '', extra: opt.extra || '', isEnabled: opt.isEnabled });
    setOptionErrors({});
    setShowOptionForm(true);
  };

  const handleSaveOption = async () => {
    const errs = {};
    if (!optionForm.name.trim()) errs.name = 'Name is required';
    if (!optionForm.type.trim()) errs.type = 'Type is required';
    setOptionErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setOptionSaving(true);
    setOptionMessage({ type: '', text: '' });
    try {
      if (editingOption) {
        await api.put(`/settings/options/${editingOption.id}`, optionForm);
        setOptionMessage({ type: 'success', text: 'Option updated successfully!' });
      } else {
        await api.post('/settings/options', optionForm);
        setOptionMessage({ type: 'success', text: 'Option added successfully!' });
      }
      resetOptionForm();
      fetchOptions(optionForm.type);
      fetchOptionTypes();
    } catch (error) {
      setOptionMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save option' });
    } finally {
      setOptionSaving(false);
    }
  };

  const handleDeleteOption = async (id) => {
    if (!window.confirm('Are you sure you want to delete this option?')) return;
    try {
      await api.delete(`/settings/options/${id}`);
      setOptionMessage({ type: 'success', text: 'Option deleted!' });
      fetchOptions(selectedOptionType);
    } catch (error) {
      setOptionMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete option' });
    }
  };

  const handleToggleOption = async (id) => {
    try {
      await api.patch(`/settings/options/${id}/toggle`);
      fetchOptions(selectedOptionType);
    } catch (error) {
      setOptionMessage({ type: 'error', text: error.response?.data?.error || 'Failed to toggle option' });
    }
  };

  const handleAddNewType = () => {
    const t = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!t) return;
    if (optionTypes.includes(t)) {
      setOptionMessage({ type: 'error', text: `Type "${t}" already exists.` });
      return;
    }
    setSelectedOptionType(t);
    setOptionForm(prev => ({ ...prev, type: t }));
    setNewTypeName('');
    setOptionSearch('');
    setOptions([]);
    setOptionTypes(prev => [...prev, t]);
  };

  const handleDeleteType = async (type) => {
    if (!window.confirm(`Delete the "${type}" type and all its options?`)) return;
    try {
      await api.delete(`/settings/options/type/${type}`);
      setOptionMessage({ type: 'success', text: `Type "${type}" deleted!` });
      const remaining = optionTypes.filter(t => t !== type);
      setOptionTypes(remaining);
      const next = remaining.length > 0 ? remaining[0] : 'country';
      setSelectedOptionType(next);
      setOptionSearch('');
      fetchOptions(next);
    } catch (error) {
      setOptionMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete type' });
    }
  };

  if (loading || gatewaysLoading || firebaseLoading || currenciesLoading || optionsLoading) {
    return (
      <AdminLayout>
        <div className="config-loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-config-page">
        <div className="config-header">
          <h1>Configuration</h1>
          <p>Manage system configuration settings</p>
        </div>

        {/* ======= SMTP SECTION ======= */}
        <div className="config-section">
          <div className="config-section-header">
            <div className="config-section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div>
              <h2>Email Verification (SMTP)</h2>
              <p>Configure SMTP settings for sending verification emails to users during registration</p>
            </div>
          </div>

          {message.text && (
            <div className={`config-message ${message.type}`}>
              <span className="config-message-icon">
                {message.type === 'success' ? '✓' : '✕'}
              </span>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSave} className="config-form">
            <div className="config-form-grid">
              <div className="config-form-group">
                <label htmlFor="smtpHost">SMTP Host <span className="required">*</span></label>
                <input
                  type="text"
                  id="smtpHost"
                  name="smtpHost"
                  value={formData.smtpHost}
                  onChange={handleChange}
                  placeholder="e.g. smtp.gmail.com"
                  className={errors.smtpHost ? 'error' : ''}
                />
                {errors.smtpHost && <span className="error-text">{errors.smtpHost}</span>}
              </div>

              <div className="config-form-group">
                <label htmlFor="smtpPort">SMTP Port <span className="required">*</span></label>
                <input
                  type="number"
                  id="smtpPort"
                  name="smtpPort"
                  value={formData.smtpPort}
                  onChange={handleChange}
                  placeholder="e.g. 587"
                  className={errors.smtpPort ? 'error' : ''}
                />
                {errors.smtpPort && <span className="error-text">{errors.smtpPort}</span>}
              </div>

              <div className="config-form-group">
                <label htmlFor="senderEmail">Email Address (Sender Email) <span className="required">*</span></label>
                <input
                  type="email"
                  id="senderEmail"
                  name="senderEmail"
                  value={formData.senderEmail}
                  onChange={handleChange}
                  placeholder="e.g. noreply@gulfconstructor.com"
                  className={errors.senderEmail ? 'error' : ''}
                />
                {errors.senderEmail && <span className="error-text">{errors.senderEmail}</span>}
              </div>

              <div className="config-form-group">
                <label htmlFor="senderPassword">Email Password / App Password <span className="required">*</span></label>
                <input
                  type="password"
                  id="senderPassword"
                  name="senderPassword"
                  value={formData.senderPassword}
                  onChange={handleChange}
                  placeholder="Enter email password or app password"
                  className={errors.senderPassword ? 'error' : ''}
                />
                {errors.senderPassword && <span className="error-text">{errors.senderPassword}</span>}
              </div>

              <div className="config-form-group">
                <label htmlFor="encryption">Encryption Type <span className="required">*</span></label>
                <select
                  id="encryption"
                  name="encryption"
                  value={formData.encryption}
                  onChange={handleChange}
                  className={errors.encryption ? 'error' : ''}
                >
                  <option value="TLS">TLS</option>
                  <option value="SSL">SSL</option>
                </select>
                {errors.encryption && <span className="error-text">{errors.encryption}</span>}
              </div>
            </div>

            <div className="config-form-actions">
              <button
                type="button"
                className="config-btn config-btn-outline"
                onClick={handleTest}
                disabled={testing || saving}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                type="submit"
                className="config-btn config-btn-primary"
                disabled={saving || testing}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>

        {/* ======= PAYMENT GATEWAYS SECTION ======= */}
        <div className="config-section">
          <div className="config-section-header">
            <div className="config-section-icon pg-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div>
              <h2>Payment Gateways</h2>
              <p>Configure payment gateway credentials. Only enabled gateways will be shown to users during checkout.</p>
            </div>
          </div>

          <div className="pg-cards">
            {gateways.map(gw => {
              const info = GATEWAY_INFO[gw.name] || {};
              const form = gatewayForms[gw.name] || {};
              const errs = gatewayErrors[gw.name] || {};
              const msg = gatewayMessage[gw.name];
              const isSaving = gatewaySaving[gw.name];
              const isTesting = gatewayTesting[gw.name];

              return (
                <div
                  key={gw.name}
                  className={`pg-card ${form.isEnabled ? 'pg-card-enabled' : ''}`}
                  style={{ '--gw-color': info.color || '#1a3a5c' }}
                >
                  <div className="pg-card-header">
                    <div className="pg-card-brand">
                      <span className="pg-card-icon">{info.icon}</span>
                      <div>
                        <h3>{gw.displayName}</h3>
                        <span className={`pg-card-badge ${form.isEnabled ? 'active' : 'inactive'}`}>
                          {form.isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                    <label className="pg-toggle">
                      <input
                        type="checkbox"
                        checked={form.isEnabled || false}
                        onChange={() => handleGatewayToggle(gw.name)}
                      />
                      <span className="pg-toggle-slider"></span>
                    </label>
                  </div>

                  {msg && (
                    <div className={`config-message ${msg.type}`}>
                      <span className="config-message-icon">{msg.type === 'success' ? '✓' : '✕'}</span>
                      {msg.text}
                    </div>
                  )}

                  <div className="pg-card-body">
                    <div className="config-form-group">
                      <label>{info.apiLabel || 'API Key'} <span className="required">*</span></label>
                      <input
                        type="text"
                        value={form.apiKey || ''}
                        onChange={e => handleGatewayChange(gw.name, 'apiKey', e.target.value)}
                        placeholder={`Enter ${gw.displayName} ${info.apiLabel || 'API Key'}`}
                        className={errs.apiKey ? 'error' : ''}
                      />
                      {errs.apiKey && <span className="error-text">{errs.apiKey}</span>}
                    </div>

                    <div className="config-form-group">
                      <label>{info.secretLabel || 'Secret Key'} <span className="required">*</span></label>
                      <input
                        type="password"
                        value={form.secretKey || ''}
                        onChange={e => handleGatewayChange(gw.name, 'secretKey', e.target.value)}
                        placeholder={`Enter ${gw.displayName} ${info.secretLabel || 'Secret Key'}`}
                        className={errs.secretKey ? 'error' : ''}
                      />
                      {errs.secretKey && <span className="error-text">{errs.secretKey}</span>}
                    </div>

                    {info.hasMode && (
                      <div className="config-form-group">
                        <label>Environment Mode</label>
                        <select
                          value={form.mode || 'test'}
                          onChange={e => handleGatewayChange(gw.name, 'mode', e.target.value)}
                        >
                          <option value="test">Sandbox / Test</option>
                          <option value="live">Live / Production</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="pg-card-actions">
                    <button
                      type="button"
                      className="config-btn config-btn-outline config-btn-sm"
                      onClick={() => handleGatewayTest(gw.name)}
                      disabled={isTesting || isSaving}
                    >
                      {isTesting ? 'Testing...' : 'Test'}
                    </button>
                    <button
                      type="button"
                      className="config-btn config-btn-primary config-btn-sm"
                      onClick={() => handleGatewaySave(gw.name)}
                      disabled={isSaving || isTesting}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ======= FIREBASE SMS VERIFICATION SECTION ======= */}
        <div className="config-section">
          <div className="config-section-header">
            <div className="config-section-icon sms-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
            </div>
            <div>
              <h2>SMS Verification (Firebase)</h2>
              <p>Configure Firebase Authentication for phone number OTP verification during registration and login</p>
            </div>
          </div>

          {firebaseMessage.text && (
            <div className={`config-message ${firebaseMessage.type}`}>
              <span className="config-message-icon">
                {firebaseMessage.type === 'success' ? '✓' : '✕'}
              </span>
              {firebaseMessage.text}
            </div>
          )}

          <div className="firebase-card">
            <div className="firebase-card-header">
              <div className="firebase-brand">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M3.89 15.672L6.255 1.618a.406.406 0 01.755-.112l2.52 4.703-5.64 9.463z" fill="#FFA000"/>
                  <path d="M19.885 18.336l-1.598-9.895a.405.405 0 00-.69-.208L3.89 15.672l6.962 4.342a1.218 1.218 0 001.272.002l7.761-5.68z" fill="#F57C00"/>
                  <path d="M14.3 7.145L11.87 2.61a.405.405 0 00-.718.012L3.89 15.672 14.3 7.145z" fill="#FFCA28"/>
                  <path d="M19.885 18.336l-1.598-9.895a.405.405 0 00-.69-.208l-3.297 2.912 4.098 7.191 1.487-3.68v3.68z" fill="#F57C00" opacity=".5"/>
                </svg>
                <div>
                  <h3>Firebase Authentication</h3>
                  <span className={`pg-card-badge ${firebaseForm.isEnabled ? 'active' : 'inactive'}`}>
                    {firebaseForm.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <label className="pg-toggle">
                <input
                  type="checkbox"
                  checked={firebaseForm.isEnabled}
                  onChange={handleFirebaseToggle}
                />
                <span className="pg-toggle-slider"></span>
              </label>
            </div>

            <div className="pg-card-body">
              <div className="config-form-grid">
                <div className="config-form-group">
                  <label htmlFor="fb-apiKey">Firebase API Key <span className="required">*</span></label>
                  <input
                    type="text"
                    id="fb-apiKey"
                    name="apiKey"
                    value={firebaseForm.apiKey}
                    onChange={handleFirebaseChange}
                    placeholder="e.g. AIzaSyB..."
                    className={firebaseErrors.apiKey ? 'error' : ''}
                  />
                  {firebaseErrors.apiKey && <span className="error-text">{firebaseErrors.apiKey}</span>}
                </div>

                <div className="config-form-group">
                  <label htmlFor="fb-authDomain">Auth Domain <span className="required">*</span></label>
                  <input
                    type="text"
                    id="fb-authDomain"
                    name="authDomain"
                    value={firebaseForm.authDomain}
                    onChange={handleFirebaseChange}
                    placeholder="e.g. myproject.firebaseapp.com"
                    className={firebaseErrors.authDomain ? 'error' : ''}
                  />
                  {firebaseErrors.authDomain && <span className="error-text">{firebaseErrors.authDomain}</span>}
                </div>

                <div className="config-form-group">
                  <label htmlFor="fb-projectId">Project ID <span className="required">*</span></label>
                  <input
                    type="text"
                    id="fb-projectId"
                    name="projectId"
                    value={firebaseForm.projectId}
                    onChange={handleFirebaseChange}
                    placeholder="e.g. my-project-id"
                    className={firebaseErrors.projectId ? 'error' : ''}
                  />
                  {firebaseErrors.projectId && <span className="error-text">{firebaseErrors.projectId}</span>}
                </div>

                <div className="config-form-group">
                  <label htmlFor="fb-messagingSenderId">Messaging Sender ID <span className="required">*</span></label>
                  <input
                    type="text"
                    id="fb-messagingSenderId"
                    name="messagingSenderId"
                    value={firebaseForm.messagingSenderId}
                    onChange={handleFirebaseChange}
                    placeholder="e.g. 123456789012"
                    className={firebaseErrors.messagingSenderId ? 'error' : ''}
                  />
                  {firebaseErrors.messagingSenderId && <span className="error-text">{firebaseErrors.messagingSenderId}</span>}
                </div>
              </div>

              <div className="config-form-actions">
                <button
                  type="button"
                  className="config-btn config-btn-primary"
                  onClick={handleFirebaseSave}
                  disabled={firebaseSaving}
                >
                  {firebaseSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ======= CURRENCY SETTINGS SECTION ======= */}
        <div className="config-section">
          <div className="config-section-header">
            <div className="config-section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
            </div>
            <div>
              <h2>Currency Settings</h2>
              <p>Manage currencies, exchange rates, and set the default currency for the platform</p>
            </div>
          </div>

          {currencyMessage.text && (
            <div className={`config-message ${currencyMessage.type}`}>
              <span className="config-message-icon">{currencyMessage.type === 'success' ? '✓' : '✕'}</span>
              {currencyMessage.text}
            </div>
          )}

          <div className="config-currency-section">
            <div className="config-table-header">
              <h3>Currencies ({currencies.length})</h3>
              <button className="config-btn config-btn-add" onClick={() => { resetCurrencyForm(); setShowCurrencyForm(true); }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                Add Currency
              </button>
            </div>

            {showCurrencyForm && (
              <div className="config-inline-form">
                <h4>{editingCurrency ? 'Edit Currency' : 'Add New Currency'}</h4>
                <div className="config-form-grid">
                  <div className="config-form-group">
                    <label>Code <span className="required">*</span></label>
                    <input type="text" value={currencyForm.code} onChange={e => setCurrencyForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} placeholder="e.g. USD" maxLength={5} className={currencyErrors.code ? 'error' : ''} />
                    {currencyErrors.code && <span className="error-text">{currencyErrors.code}</span>}
                  </div>
                  <div className="config-form-group">
                    <label>Name <span className="required">*</span></label>
                    <input type="text" value={currencyForm.name} onChange={e => setCurrencyForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. US Dollar" className={currencyErrors.name ? 'error' : ''} />
                    {currencyErrors.name && <span className="error-text">{currencyErrors.name}</span>}
                  </div>
                  <div className="config-form-group">
                    <label>Symbol <span className="required">*</span></label>
                    <input type="text" value={currencyForm.symbol} onChange={e => setCurrencyForm(prev => ({ ...prev, symbol: e.target.value }))} placeholder="e.g. $" maxLength={5} className={currencyErrors.symbol ? 'error' : ''} />
                    {currencyErrors.symbol && <span className="error-text">{currencyErrors.symbol}</span>}
                  </div>
                  <div className="config-form-group">
                    <label>Exchange Rate (vs USD) <span className="required">*</span></label>
                    <input type="number" step="0.0001" value={currencyForm.exchangeRate} onChange={e => setCurrencyForm(prev => ({ ...prev, exchangeRate: e.target.value }))} placeholder="1.00" className={currencyErrors.exchangeRate ? 'error' : ''} />
                    {currencyErrors.exchangeRate && <span className="error-text">{currencyErrors.exchangeRate}</span>}
                  </div>
                </div>
                <div className="config-form-row-inline">
                  <label className="config-checkbox-label">
                    <input type="checkbox" checked={currencyForm.isEnabled} onChange={e => setCurrencyForm(prev => ({ ...prev, isEnabled: e.target.checked }))} />
                    Enabled
                  </label>
                  <label className="config-checkbox-label">
                    <input type="checkbox" checked={currencyForm.isDefault} onChange={e => setCurrencyForm(prev => ({ ...prev, isDefault: e.target.checked }))} />
                    Set as Default
                  </label>
                </div>
                <div className="config-form-actions">
                  <button className="config-btn config-btn-secondary" onClick={resetCurrencyForm}>Cancel</button>
                  <button className="config-btn config-btn-primary" onClick={handleSaveCurrency} disabled={currencySaving}>
                    {currencySaving ? 'Saving...' : (editingCurrency ? 'Update Currency' : 'Add Currency')}
                  </button>
                </div>
              </div>
            )}

            {currencies.length > 0 ? (
              <div className="config-data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Symbol</th>
                      <th>Exchange Rate</th>
                      <th>Status</th>
                      <th>Default</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currencies.map(c => (
                      <tr key={c.id} className={!c.isEnabled ? 'disabled-row' : ''}>
                        <td><strong>{c.code}</strong></td>
                        <td>{c.name}</td>
                        <td className="currency-symbol-cell">{c.symbol}</td>
                        <td>{c.exchangeRate}</td>
                        <td>
                          <span className={`config-status-badge ${c.isEnabled ? 'active' : 'disabled'}`}>
                            {c.isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          {c.isDefault ? (
                            <span className="config-status-badge default">Default</span>
                          ) : (
                            <button className="config-btn-text" onClick={() => handleSetDefaultCurrency(c.id)}>Set Default</button>
                          )}
                        </td>
                        <td className="config-actions-cell">
                          <button className="config-btn-icon edit" onClick={() => handleEditCurrency(c)} title="Edit">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          {!c.isDefault && (
                            <button className="config-btn-icon delete" onClick={() => handleDeleteCurrency(c.id)} title="Delete">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="config-empty-state">
                <p>No currencies configured yet. Add your first currency to get started.</p>
              </div>
            )}
          </div>
        </div>

        {/* ======= MULTI OPTION SETTINGS SECTION ======= */}
        <div className="config-section">
          <div className="config-section-header">
            <div className="config-section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
            </div>
            <div>
              <h2>Multi Option Settings</h2>
              <p>Manage system options like countries, regions, and other configurable lists</p>
            </div>
          </div>

          {optionMessage.text && (
            <div className={`config-message ${optionMessage.type}`}>
              <span className="config-message-icon">{optionMessage.type === 'success' ? '✓' : '✕'}</span>
              {optionMessage.text}
            </div>
          )}

          <div className="config-options-section">
            <div className="config-options-type-bar">
              <div className="config-type-tabs">
                {(optionTypes.length > 0 ? optionTypes : ['country']).map(type => (
                  <div key={type} className={`config-type-tab ${selectedOptionType === type ? 'active' : ''}`}>
                    <span onClick={() => handleTypeChange(type)}>
                      {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    {selectedOptionType === type && type !== 'country' && (
                      <button className="config-type-delete" onClick={(e) => { e.stopPropagation(); handleDeleteType(type); }} title="Delete type">
                        &times;
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="config-table-header">
              <h3>{selectedOptionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Options ({options.length})</h3>
              <div className="config-search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  type="text"
                  value={optionSearch}
                  onChange={e => setOptionSearch(e.target.value)}
                  placeholder={`Search ${selectedOptionType.replace(/_/g, ' ')}...`}
                />
              </div>
              <button className="config-btn config-btn-add" onClick={() => { resetOptionForm(); setOptionForm(prev => ({ ...prev, type: selectedOptionType })); setShowOptionForm(true); }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                Add Option
              </button>
            </div>

            {showOptionForm && (
              <div className="config-inline-form">
                <h4>{editingOption ? 'Edit Option' : 'Add New Option'}</h4>
                <div className="config-form-grid">
                  <div className="config-form-group">
                    <label>Name <span className="required">*</span></label>
                    <input type="text" value={optionForm.name} onChange={e => setOptionForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. United States" className={optionErrors.name ? 'error' : ''} />
                    {optionErrors.name && <span className="error-text">{optionErrors.name}</span>}
                  </div>
                </div>
                <div className="config-form-row-inline">
                  <label className="config-checkbox-label">
                    <input type="checkbox" checked={optionForm.isEnabled} onChange={e => setOptionForm(prev => ({ ...prev, isEnabled: e.target.checked }))} />
                    Enabled
                  </label>
                </div>
                <div className="config-form-actions">
                  <button className="config-btn config-btn-secondary" onClick={resetOptionForm}>Cancel</button>
                  <button className="config-btn config-btn-primary" onClick={handleSaveOption} disabled={optionSaving}>
                    {optionSaving ? 'Saving...' : (editingOption ? 'Update Option' : 'Add Option')}
                  </button>
                </div>
              </div>
            )}

            {options.length > 0 ? (
              <div className="config-data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {options.filter(opt => {
                      if (!optionSearch.trim()) return true;
                      const q = optionSearch.toLowerCase();
                      return (opt.name || '').toLowerCase().includes(q);
                    }).map(opt => (
                      <tr key={opt.id} className={!opt.isEnabled ? 'disabled-row' : ''}>
                        <td><strong>{opt.name}</strong></td>
                        <td>
                          <span className={`config-status-badge ${opt.isEnabled ? 'active' : 'disabled'}`}>
                            {opt.isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="config-actions-cell">
                          <button className="config-btn-icon toggle" onClick={() => handleToggleOption(opt.id)} title={opt.isEnabled ? 'Disable' : 'Enable'}>
                            {opt.isEnabled ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2"/></svg>
                            )}
                          </button>
                          <button className="config-btn-icon edit" onClick={() => handleEditOption(opt)} title="Edit">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                          <button className="config-btn-icon delete" onClick={() => handleDeleteOption(opt.id)} title="Delete">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="config-empty-state">
                <p>No options configured for "{selectedOptionType.replace(/_/g, ' ')}". Add your first option to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminConfigurationPage;
